// Model settings and their effective values. Saved choices are never rewritten on render.
import { useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  MenuItem,
  Slider,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import TuneIcon from "@mui/icons-material/Tune";
import PvThermalSettings, { thermalPresetLabel } from "./PvThermalSettings";
import { resolveThermalSettings } from "../config/thermalSettings";
import { NEW_PROJECT_THERMAL_MODEL, NEW_PROJECT_THERMAL_SETTINGS, isNewProjectThermalSettings } from "../config/newProjectDefaults";
import SunPathDialog from "./SunPathDialog";
import { useIsGreek, useTranslateText } from "../../context/LanguageContext";
import { AccessibleSelect as Select } from "../form/AccessibleSelectFields";

// The option text is also rendered inside MUI menus, outside the legacy DOM
// translation bridge. Keep scientific model names unchanged in both languages.
const MODEL_NAMES = new Set([
  "Perez", "Hay-Davies", "ASHRAE", "Martín-Ruiz",
  "SAPM", "Faiman", "Sandia", "PVWatts",
  "CEC", "De Soto", "PVsyst",
]);
const GREEK_MODEL_TEXT = {
  "Row sky masking": "Απόκρυψη ουρανού από τις σειρές",
  "Row shadow on the panel": "Σκιά σειρών επάνω στα πλαίσια",
  "Module orientation": "Προσανατολισμός πλαισίου",
  "Spectral correction": "Φασματική διόρθωση",
  "Row shadow": "Σκίαση μεταξύ σειρών",
  "Horizon shading": "Σκίαση ορίζοντα",
  "Transposition": "Μεταφορά ακτινοβολίας",
  "Incidence angle": "Γωνία πρόσπτωσης",
  "Conversion (AC)": "Μετατροπή σε AC",
  "DC model": "Μοντέλο DC",
  "Module": "Πλαίσιο",
  "Inverter": "Αντιστροφέας",
  "Ground surface": "Επιφάνεια εδάφους",
  "Isotropic": "Ισότροπο",
  "Physical": "Φυσικό μοντέλο",
  "First Solar broadband": "First Solar — ευρυζωνικό",
  "From datasheet": "Από το φύλλο δεδομένων",
  " Using this module's own coefficients.": " Χρησιμοποιούνται οι συντελεστές του επιλεγμένου πλαισίου.",
  " This module's datasheet has no coefficients for it, so standard literature values are used.": " Το φύλλο δεδομένων του πλαισίου δεν διαθέτει σχετικούς συντελεστές· χρησιμοποιούνται τυπικές τιμές της βιβλιογραφίας.",
  "Portrait — all 3 bypassed": "Κατακόρυφα — παράκαμψη και των 3",
  "Landscape — 1 of 3 bypassed": "Οριζόντια — παράκαμψη 1 από τα 3",
  "shadow": "σκιά",
  "still": "συνεχίζουν",
  "producing": "να παράγουν",
  "is bifacial, so the rear side has to be modelled — which only the detailed row field does. It is set for you here, and the simplified model is unavailable until a monofacial module is selected.": "είναι διπλής όψης, οπότε απαιτείται υπολογισμός και της πίσω όψης. Εφαρμόζεται αυτόματα το λεπτομερές μοντέλο σειρών. Το απλοποιημένο μοντέλο γίνεται διαθέσιμο όταν επιλεγεί πλαίσιο μονής όψης.",
  "The detailed model cannot use": "Το λεπτομερές μοντέλο δεν υποστηρίζει το",
  "is used for the sky instead — about 2% lower. That costs more than the 0.3% the detailed model gains. Switch to the simplified model to keep Perez, or pick Hay-Davies above so the sky model on record matches what runs.": "χρησιμοποιείται για τον ουρανό, με αποτέλεσμα περίπου 2% χαμηλότερο. Η διαφορά αυτή υπερβαίνει το όφελος περίπου 0,3% του λεπτομερούς μοντέλου. Επιλέξτε το απλοποιημένο μοντέλο για να διατηρήσετε το Perez ή επιλέξτε Hay-Davies στη ρύθμιση διάχυτης ακτινοβολίας.",
  "Separate from the sky masking above: that is the light rows hide, this is the shadow they cast. Only bites at low sun, and grows sharply as rows get closer.": "Η απόκρυψη ουρανού αφορά τη διάχυτη ακτινοβολία. Εδώ υπολογίζεται η σκιά που ρίχνει η μία σειρά στην άλλη, όταν ο ήλιος βρίσκεται χαμηλά. Η επίδραση αυξάνεται όσο οι σειρές πλησιάζουν.",
  "Electrical effect": "Ηλεκτρική επίδραση της σκίασης",
  "How completely a shaded substring drops out. 100% is the textbook diode behaviour; lower values blend toward a plain area loss, which is closer to what real arrays show because cells are not perfectly matched.": "Βαθμός επίδρασης της παράκαμψης ενός σκιασμένου τμήματος κυψελών. Το 100% αντιστοιχεί στην πλήρη επίδραση του μοντέλου διόδων. Χαμηλότερες τιμές προσεγγίζουν απώλεια ανάλογη της σκιασμένης επιφάνειας.",
  "Horizon (terrain) shading": "Σκίαση από το ανάγλυφο του ορίζοντα",
  "This terrain profile was measured at": "Το αποθηκευμένο προφίλ αναγλύφου αφορά τη θέση",
  "— the project is now at": "— το έργο βρίσκεται πλέον στη θέση",
  ". A horizon belongs to a location, so this one does not describe this site. Refresh it.": ". Το προφίλ ορίζοντα εξαρτάται από την τοποθεσία. Ανανεώστε το για τη νέα θέση.",
  "Remove": "Αφαίρεση",
  "View sun path and terrain": "Προβολή τροχιάς ήλιου και αναγλύφου",
  "This run uses": "Στην εκτέλεση χρησιμοποιείται το",
  ", not": ", αντί για το",
  "Hay-Davies runs about 1-2% below Perez on a tilted plane, and the report states the model that ran. Pick Hay-Davies here to make the setting on record match it.": "Το Hay-Davies δίνει περίπου 1–2% χαμηλότερη ακτινοβολία από το Perez σε κεκλιμένο επίπεδο. Η αναφορά καταγράφει το μοντέλο που χρησιμοποιήθηκε. Επιλέξτε εδώ Hay-Davies ώστε η αποθηκευμένη επιλογή να συμφωνεί με την εκτέλεση.",
  "is a SAPM module, so its angular response comes from the datasheet's own B0–B5 coefficients and this setting has no effect.": "χρησιμοποιεί μοντέλο SAPM. Η γωνιακή απόκριση προκύπτει από τους συντελεστές B0–B5 του φύλλου δεδομένων και η παρούσα επιλογή δεν επηρεάζει τον υπολογισμό.",
  "For this module, Automatic resolves to": "Για αυτό το πλαίσιο, η αυτόματη επιλογή καταλήγει στο",
  "This module": "Το πλαίσιο",
  "IAM set by module": "IAM από το πλαίσιο",
  "Uses the row spacing and height from the layout. Within 0.3% of the simplified model on the same sky model.": "Χρησιμοποιεί την απόσταση και το ύψος των σειρών από τη διάταξη. Η απόκλιση από το απλοποιημένο μοντέλο είναι εντός 0,3% για το ίδιο μοντέλο ουρανού.",
  "Uses the tilt and ground coverage ratio. Within 0.3% of the detailed model on the same sky model.": "Χρησιμοποιεί την κλίση και τον λόγο κάλυψης εδάφους. Η απόκλιση από το λεπτομερές μοντέλο είναι εντός 0,3% για το ίδιο μοντέλο ουρανού.",
  "Flat terrain": "Επίπεδο έδαφος",
  "The land around this site is level, so the terrain profile makes no difference to the result.": "Το έδαφος γύρω από τη θέση είναι επίπεδο· το προφίλ αναγλύφου δεν επηρεάζει το αποτέλεσμα.",
  "Hills block the sun early and late in the day. Those hours are removed from the direct beam.": "Οι λόφοι κρύβουν τον ήλιο στην αρχή και στο τέλος της ημέρας. Τις αντίστοιχες ώρες αποκλείεται η άμεση ακτινοβολία.",
  "Not set. Fetch the surrounding terrain to account for hills blocking the sun at low angles. Makes no difference on level ground.": "Δεν έχει οριστεί. Λάβετε το προφίλ αναγλύφου για να υπολογιστεί η απόκρυψη του ήλιου από λόφους σε χαμηλές γωνίες. Δεν επηρεάζει το αποτέλεσμα σε επίπεδο έδαφος.",
  "Fetching…": "Λήψη…",
  "Refresh": "Ανανέωση",
  "Fetch terrain": "Λήψη αναγλύφου",
  "A bifacial module is modelled as a row field, and that model implements Hay-Davies and isotropic only.": "Τα πλαίσια διπλής όψης υπολογίζονται με το μοντέλο σειρών, το οποίο υποστηρίζει μόνο Hay-Davies και ισότροπο ουρανό.",
  "The detailed row model implements Hay-Davies and isotropic only.": "Το λεπτομερές μοντέλο σειρών υποστηρίζει μόνο Hay-Davies και ισότροπο ουρανό.",
  " is bifacial, so the rear side has to be modelled — which only the detailed row field does. It is set for you here, and the simplified model is unavailable until a monofacial module is selected.": " είναι διπλής όψης, οπότε απαιτείται υπολογισμός και της πίσω όψης. Εφαρμόζεται αυτόματα το λεπτομερές μοντέλο σειρών. Το απλοποιημένο μοντέλο γίνεται διαθέσιμο όταν επιλεγεί πλαίσιο μονής όψης.",
  "The detailed model cannot use ": "Το λεπτομερές μοντέλο δεν υποστηρίζει το ",
  " — the project is now at ": " — το έργο βρίσκεται πλέον στη θέση ",
  "This run uses ": "Στην εκτέλεση χρησιμοποιείται το ",
  " is a SAPM module, so its angular response comes from the datasheet's own B0–B5 coefficients and this setting has no effect.": " χρησιμοποιεί μοντέλο SAPM. Η γωνιακή απόκριση προκύπτει από τους συντελεστές B0–B5 του φύλλου δεδομένων και η παρούσα επιλογή δεν επηρεάζει τον υπολογισμό.",
  "Cell temperature": "Μοντέλο θερμοκρασίας",
  "Transposition (diffuse sky)": "Διάχυτη ακτινοβολία στο επίπεδο πλαισίων",
  "Incidence angle (IAM)": "Γωνία πρόσπτωσης (IAM)",
  "Conversion (inverter AC model)": "Μοντέλο αντιστροφέα DC → AC",
  "Thermal coefficients": "Θερμικοί συντελεστές",
  Automatic: "Αυτόματο",
  "Thermal balance with Uc and Uv. Match these coefficients to the comparison study.": "Θερμικό ισοζύγιο με Uc και Uv. Χρησιμοποιήστε τους συντελεστές της συγκριτικής μελέτης.",
  "Thermal balance with Uc and Uv, the model most comparative studies use. Wind cooling is not credited unless Uv is set.": "Θερμικό ισοζύγιο με Uc και Uv, το μοντέλο που χρησιμοποιούν οι περισσότερες συγκριτικές μελέτες. Η ψύξη από τον άνεμο δεν λαμβάνεται υπόψη, εκτός αν οριστεί Uv.",
  "Not applied": "Χωρίς διόρθωση",
  "Simplified (masking angle)": "Απλοποιημένο (γωνία κάλυψης)",
  "Detailed (view factors)": "Λεπτομερές (συντελεστές θέασης)",
  "Bypass diodes (realistic)": "Δίοδοι παράκαμψης (ρεαλιστικό)",
  "Linear (area only)": "Γραμμικό (μόνο επιφάνεια)",
  "Not modelled": "Χωρίς μοντελοποίηση",
  "“Not modelled” does not apply to this layout: its calculated shading already includes the rows, with the obstacles and the other faces, and it is applied. Choose a shadow model, so the row shadow is not counted a second time in the DC shading percentage.":
    "Η «Χωρίς μοντελοποίηση» δεν ισχύει σε αυτή τη διάταξη: η σκίαση που υπολογίστηκε περιλαμβάνει ήδη τις σειρές, μαζί με τα εμπόδια και τις άλλες πλευρές, και εφαρμόζεται. Επιλέξτε μοντέλο σκιάς, ώστε η σκιά των σειρών να μη μετρηθεί δεύτερη φορά στο ποσοστό σκίασης DC.",
  "“Not modelled” is not offered: the layout's calculated shading includes the rows themselves.":
    "Η «Χωρίς μοντελοποίηση» δεν διατίθεται: η σκίαση που υπολογίστηκε στη διάταξη περιλαμβάνει τις ίδιες τις σειρές.",
  "Portrait (upright)": "Κατακόρυφη τοποθέτηση",
  "Landscape (on its side)": "Οριζόντια τοποθέτηση",
  Portrait: "Κατακόρυφη τοποθέτηση",
  Landscape: "Οριζόντια τοποθέτηση",
  "Bypass diodes": "Δίοδοι παράκαμψης",
  "Generic values": "Τυπικές τιμές",
  "Open rack — glass/polymer": "Ανοιχτή βάση — γυαλί/πολυμερές",
  "Open rack — glass/glass": "Ανοιχτή βάση — γυαλί/γυαλί",
  "Close roof mount — glass/glass": "Κοντινή στήριξη στη στέγη — γυαλί/γυαλί",
  "Insulated back — glass/polymer": "Μονωμένη πίσω όψη — γυαλί/πολυμερές",
  "Models circumsolar and horizon brightening separately. The industry reference for tilted-plane irradiance and the most accurate on clear days.": "Υπολογίζει χωριστά την αυξημένη ακτινοβολία γύρω από τον ήλιο και στον ορίζοντα. Συνήθης επιλογή αναφοράς για κεκλιμένα επίπεδα, ιδιαίτερα σε καθαρό ουρανό.",
  "Circumsolar only, no horizon band. Slightly conservative against Perez; useful when comparing with a study that used it.": "Λαμβάνει υπόψη την ακτινοβολία γύρω από τον ήλιο, χωρίς ξεχωριστή ζώνη ορίζοντα. Συνήθως δίνει λίγο χαμηλότερο αποτέλεσμα από το Perez.",
  "Treats diffuse light as uniform across the sky. Simplest and consistently the most conservative — mainly a sanity-check floor.": "Θεωρεί τη διάχυτη ακτινοβολία ομοιόμορφη στον ουρανό. Απλό και συντηρητικό μοντέλο, χρήσιμο ως έλεγχος σύγκρισης.",
  "Lets the engine pick to suit the module's DC model. The right choice unless you are matching an external study.": "Επιλέγει αυτόματα το κατάλληλο μοντέλο για το πλαίσιο. Αλλάξτε το μόνο για σύγκριση με άλλη μελέτη.",
  "Fresnel reflection through the glass from first principles. A safe explicit choice when the datasheet carries no IAM coefficients.": "Υπολογίζει την ανάκλαση στο γυαλί με τις εξισώσεις Fresnel. Κατάλληλη επιλογή όταν δεν υπάρχουν συντελεστές IAM στο φύλλο δεδομένων.",
  "Single-parameter empirical fit. Long-established and fast, but loses accuracy past roughly 70° incidence.": "Εμπειρικό μοντέλο μίας παραμέτρου. Γρήγορο, αλλά λιγότερο ακριβές για γωνίες πρόσπτωσης πάνω από περίπου 70°.",
  "Holds up better at very shallow angles, so it suits low tilts and high-latitude sites.": "Διατηρεί καλύτερη ακρίβεια σε πολύ πλάγιες γωνίες πρόσπτωσης· ταιριάζει σε μικρές κλίσεις και βόρειες τοποθεσίες.",
  "Uses the thermal preset and coefficients selected below.": "Λαμβάνει υπόψη τη διαμόρφωση στήριξης: μια ανοιχτή βάση και μια μονωμένη στέγη δίνουν διαφορετικές θερμοκρασίες κυψέλης.",
  "Two-parameter model of heat transfer and wind cooling. Coefficients are shown below.": "Μοντέλο δύο παραμέτρων με επίδραση του ανέμου. Οι ενεργοί συντελεστές εμφανίζονται παρακάτω.",
  "Corrects the effective irradiance for the sky's spectrum, hour by hour, from air mass and precipitable water (temperature and humidity). The model pvlib and SAM apply; a fraction of a percent over a year for crystalline silicon, more for thin film.": "Διορθώνει την ενεργό ακτινοβολία ανά ώρα με βάση την αέρια μάζα και τους υδρατμούς. Η επίδραση εξαρτάται από την τεχνολογία του πλαισίου.",
  "Holds the factor at 1.0, as a standard crystalline-silicon simulation in other packages does. Use it to lay this estimate beside one of those with that single difference removed; the corrected figure is the better estimate.": "Δεν εφαρμόζει φασματική διόρθωση (συντελεστής 1,0). Χρήσιμο για σύγκριση με μελέτη που δεν την περιλαμβάνει.",
  "Uses the inverter's OND efficiency curve when it was imported from an OND file, the detailed Sandia model when it has fitted efficiency curves, and the simpler PVWatts model otherwise.": "Χρησιμοποιεί την καμπύλη απόδοσης OND όταν ο αντιστροφέας εισήχθη από αρχείο OND, Sandia όταν υπάρχουν προσαρμοσμένες καμπύλες απόδοσης, αλλιώς PVWatts.",
  "Efficiency varies with load and DC voltage. Only available for inverters whose curves have been fitted; ignored otherwise.": "Η απόδοση μεταβάλλεται με το φορτίο και την τάση DC. Απαιτούνται προσαρμοσμένες καμπύλες απόδοσης του αντιστροφέα.",
  "Part-load efficiency curve with clipping at the inverter AC rating.": "Καμπύλη απόδοσης με το φορτίο και περιορισμό στην ονομαστική ισχύ AC.",
  "The manufacturer's efficiency curve from the inverter's OND file, used as stated, with its threshold, AC limit and night consumption; the inverter losses are reported separately, as in a PVsyst report. Only for inverters imported from an OND file.": "Η καμπύλη απόδοσης του κατασκευαστή από το αρχείο OND, όπως ορίζεται, με το κατώφλι, το όριο ισχύος AC και τη νυχτερινή κατανάλωση· οι απώλειες του αντιστροφέα εμφανίζονται χωριστά, όπως σε αναφορά PVsyst. Μόνο για αντιστροφείς που εισήχθησαν από αρχείο OND.",
  "OND efficiency curve": "Καμπύλη απόδοσης OND",
  "Corrects the diffuse light for the sky your neighbouring rows hide, using the average masking angle. Works with any sky model, Perez included.": "Διορθώνει τη διάχυτη ακτινοβολία για το τμήμα του ουρανού που κρύβουν οι γειτονικές σειρές, με βάση τη μέση γωνία κάλυψης. Υποστηρίζει και το μοντέλο Perez.",
  "Also resolves the shadow rows cast on each other and the partly shaded ground — worth about 0.3%. Cannot use Perez.": "Υπολογίζει επιπλέον τις σκιές μεταξύ σειρών και το μερικώς σκιασμένο έδαφος. Δεν υποστηρίζει το μοντέλο Perez.",
  "Front side only, so it cannot model a bifacial array. Unavailable while a bifacial module is selected.": "Υπολογίζει μόνο την εμπρόσθια όψη και δεν μπορεί να αναπαραστήσει πλαίσιο διπλής όψης. Δεν διατίθεται όσο έχει επιλεγεί τέτοιο πλαίσιο.",
  "A shadow on the bottom of a row forces bypass diodes to switch out whole substrings, so the loss jumps in steps and far exceeds the shaded area. This is what makes tight row spacing expensive.": "Η σκιά στη χαμηλή πλευρά μιας σειράς ενεργοποιεί τις διόδους παράκαμψης και αφαιρεί ολόκληρα τμήματα κυψελών. Έτσι η απώλεια μπορεί να ξεπεράσει τη σκιασμένη επιφάνεια.",
  "Loss equal to the shaded fraction of the panel. Ignores bypass diodes, so it always understates the cost — roughly eight times at typical spacing. Useful to match a study that assumed it.": "Θεωρεί την απώλεια ίση με το σκιασμένο ποσοστό του πλαισίου. Αγνοεί τις διόδους παράκαμψης και συνήθως υποεκτιμά την ηλεκτρική επίδραση.",
  "No row shadow loss from the row model. Choose this only if you are accounting for it yourself in the DC shading loss percentage. Obstacles and other faces calculated in the physical layout still apply.": "Δεν υπολογίζει απώλειες από σκίαση μεταξύ σειρών με το μοντέλο σειρών. Επιλέξτε το μόνο αν τις έχετε ήδη συμπεριλάβει στις απώλειες σκίασης DC. Τα εμπόδια και οι άλλες πλευρές που υπολογίστηκαν στη φυσική διάταξη εφαρμόζονται κανονικά.",
  "Substrings run as vertical strips, so a shadow along the bottom edge reaches all three at once and the whole module drops out. The layout builder sizes rows this way, so it is the default.": "Τα τμήματα κυψελών είναι κατακόρυφα. Μια σκιά στη χαμηλή ακμή μπορεί να επηρεάσει και τα τρία ταυτόχρονα. Αυτή είναι η προεπιλογή της διάταξης.",
  "Substrings become horizontal bands, so the shadow switches out one third at a time. Loses roughly a third as much to row shading — the reason tight-pitch ground mounts often prefer it.": "Τα τμήματα κυψελών είναι οριζόντια, ώστε η σκιά να παρακάμπτει περίπου το ένα τρίτο κάθε φορά. Μπορεί να μειώσει τις απώλειες από κοντινές σειρές.",
};
const modelText = (value, isGreek, translateText) =>
  !isGreek || MODEL_NAMES.has(value) ? value : (GREEK_MODEL_TEXT[value] || translateText(value));

// Keep in lockstep with pv_core/model_defaults.py.
export const DEFAULT_TRANSPOSITION = "perez";
export const DEFAULT_AOI_MODEL = "auto";
export const DEFAULT_THERMAL_MODEL = "sapm";
export const DEFAULT_SPECTRAL_MODEL = "first_solar";
export const DEFAULT_AC_MODEL = "auto";
export const DEFAULT_ARRAY_GEOMETRY = "free_standing";
// Lockstep with pv_core/row_shading.py.
export const DEFAULT_NEAR_SHADING_MODEL = "bypass";
export const DEFAULT_NEAR_SHADING_EFFECT = 1;
export const DEFAULT_MODULE_ORIENTATION = "portrait";

const CONTROL_WIDTH = 320;

// What pvlib's row-field model (infinite_sheds) can actually run. Anything
// else is accepted and silently treated as isotropic, so the panel resolves it
// here rather than letting the run decide quietly.
const ROW_FIELD_SKY = ["isotropic", "haydavies"];

const TRANSPOSITION_OPTIONS = [
  {
    value: "perez",
    label: "Perez",
    recommended: true,
    help: "Models circumsolar and horizon brightening separately. The industry reference for tilted-plane irradiance and the most accurate on clear days.",
  },
  {
    value: "haydavies",
    label: "Hay-Davies",
    help: "Circumsolar only, no horizon band. Slightly conservative against Perez; useful when comparing with a study that used it.",
  },
  {
    value: "isotropic",
    label: "Isotropic",
    help: "Treats diffuse light as uniform across the sky. Simplest and consistently the most conservative — mainly a sanity-check floor.",
  },
];

const AOI_OPTIONS = [
  {
    value: "auto",
    label: "Automatic",
    recommended: true,
    // Not pvlib's datasheet inference: the engine's rule is physical for
    // PVWatts modules and ASHRAE for everything else. The real resolution for
    // the selected module comes from the backend's modeling.auto_resolves_to.
    help: "Lets the engine pick to suit the module's DC model. The right choice unless you are matching an external study.",
  },
  {
    value: "physical",
    label: "Physical",
    help: "Fresnel reflection through the glass from first principles. A safe explicit choice when the datasheet carries no IAM coefficients.",
  },
  {
    value: "ashrae",
    label: "ASHRAE",
    help: "Single-parameter empirical fit. Long-established and fast, but loses accuracy past roughly 70° incidence.",
  },
  {
    value: "martin_ruiz",
    label: "Martín-Ruiz",
    help: "Holds up better at very shallow angles, so it suits low tilts and high-latitude sites.",
  },
];

const THERMAL_OPTIONS = [
  { value: "pvsyst", label: "PVsyst", recommended: true, help: "Thermal balance with Uc and Uv, the model most comparative studies use. Wind cooling is not credited unless Uv is set." },
  {
    value: "sapm",
    label: "SAPM",
    help: "Uses the thermal preset and coefficients selected below.",
  },
  {
    value: "faiman",
    label: "Faiman",
    help: "Two-parameter model of heat transfer and wind cooling. Coefficients are shown below.",
  },
];

const SPECTRAL_OPTIONS = [
  {
    value: "first_solar",
    label: "First Solar broadband",
    recommended: true,
    help: "Corrects the effective irradiance for the sky's spectrum, hour by hour, from air mass and precipitable water (temperature and humidity). The model pvlib and SAM apply; a fraction of a percent over a year for crystalline silicon, more for thin film.",
  },
  {
    value: "none",
    label: "Not applied",
    help: "Holds the factor at 1.0, as a standard crystalline-silicon simulation in other packages does. Use it to lay this estimate beside one of those with that single difference removed; the corrected figure is the better estimate.",
  },
];

const AC_OPTIONS = [
  {
    value: "auto",
    label: "Automatic",
    recommended: true,
    help: "Uses the inverter's OND efficiency curve when it was imported from an OND file, the detailed Sandia model when it has fitted efficiency curves, and the simpler PVWatts model otherwise.",
  },
  {
    value: "sandia",
    label: "Sandia",
    help: "Efficiency varies with load and DC voltage. Only available for inverters whose curves have been fitted; ignored otherwise.",
  },
  {
    value: "pvwatts",
    label: "PVWatts",
    help: "Part-load efficiency curve with clipping at the inverter AC rating.",
  },
  {
    value: "ond_curve",
    label: "OND efficiency curve",
    help: "The manufacturer's efficiency curve from the inverter's OND file, used as stated, with its threshold, AC limit and night consumption; the inverter losses are reported separately, as in a PVsyst report. Only for inverters imported from an OND file.",
  },
];

// Both options model row-to-row sky masking, and they now agree to within 0.3%
// of annual irradiance. The real trade-off is that the detailed one cannot use
// Perez, so the option text leads with that rather than with accuracy.
const GEOMETRY_OPTIONS = [
  {
    value: "free_standing",
    label: "Simplified (masking angle)",
    recommended: true,
    help: "Corrects the diffuse light for the sky your neighbouring rows hide, using the average masking angle. Works with any sky model, Perez included.",
  },
  {
    value: "row_aware",
    label: "Detailed (view factors)",
    help: "Also resolves the shadow rows cast on each other and the partly shaded ground — worth about 0.3%. Cannot use Perez.",
  },
];

// Row shading has two separate effects and they are easy to conflate. The
// geometry setting above deals with the SKY a neighbouring row hides. This one
// deals with the SHADOW it casts on the panel, whose cost is dominated by
// bypass diodes switching whole substrings out rather than by the shaded area.
const NEAR_SHADING_OPTIONS = [
  {
    value: "bypass",
    label: "Bypass diodes (realistic)",
    recommended: true,
    help: "A shadow on the bottom of a row forces bypass diodes to switch out whole substrings, so the loss jumps in steps and far exceeds the shaded area. This is what makes tight row spacing expensive.",
  },
  {
    value: "linear",
    label: "Linear (area only)",
    help: "Loss equal to the shaded fraction of the panel. Ignores bypass diodes, so it always understates the cost — roughly eight times at typical spacing. Useful to match a study that assumed it.",
  },
  {
    value: "none",
    label: "Not modelled",
    help: "No row shadow loss from the row model. Choose this only if you are accounting for it yourself in the DC shading loss percentage. Obstacles and other faces calculated in the physical layout still apply.",
  },
];

const MODULE_ORIENTATION_OPTIONS = [
  {
    value: "portrait",
    label: "Portrait (upright)",
    recommended: true,
    help: "Substrings run as vertical strips, so a shadow along the bottom edge reaches all three at once and the whole module drops out. The layout builder sizes rows this way, so it is the default.",
  },
  {
    value: "landscape",
    label: "Landscape (on its side)",
    help: "Substrings become horizontal bands, so the shadow switches out one third at a time. Loses roughly a third as much to row shading — the reason tight-pitch ground mounts often prefer it.",
  },
];

// Why one orientation loses three times as much as the other. Drawn rather
// than described: the wiring inside the module is the whole explanation, and a
// sentence about "substring orientation" convinces nobody.
function OrientationDiagram() {
  const isGreek = useIsGreek();
  const translateText = useTranslateText();
  const text = (value) => modelText(value, isGreek, translateText);
  const shaded = "var(--shade, #94a3b8)";
  const live = "#f59e0b";
  const dead = "#cbd5e1";
  return (
    <Box component="svg" viewBox="0 0 260 96" sx={{ width: "100%", maxWidth: 260, mt: 1 }}>
      {/* portrait: 3 vertical substrings, shadow across the bottom kills all 3 */}
      <text x="0" y="10" fontSize="8" fill="currentColor" opacity="0.7">{text("Portrait — all 3 bypassed")}</text>
      {[0, 1, 2].map((i) => (
        <rect key={i} x={4 + i * 14} y={16} width="12" height="56" rx="1.5" fill={dead} stroke="#94a3b8" strokeWidth="0.6" />
      ))}
      <rect x="4" y="60" width="40" height="12" fill={shaded} opacity="0.55" />
      <text x="0" y="88" fontSize="7" fill="currentColor" opacity="0.55">{text("shadow")}</text>

      {/* landscape: 3 horizontal bands, only the bottom one goes */}
      <text x="130" y="10" fontSize="8" fill="currentColor" opacity="0.7">{text("Landscape — 1 of 3 bypassed")}</text>
      {[0, 1, 2].map((i) => (
        <rect
          key={i}
          x={134}
          y={16 + i * 19}
          width="56"
          height="17"
          rx="1.5"
          fill={i === 2 ? dead : live}
          opacity={i === 2 ? 1 : 0.85}
          stroke="#94a3b8"
          strokeWidth="0.6"
        />
      ))}
      <rect x="134" y="60" width="56" height="12" fill={shaded} opacity="0.55" />
      <text x="196" y="34" fontSize="7" fill="currentColor" opacity="0.55">{text("still")}</text>
      <text x="196" y="43" fontSize="7" fill="currentColor" opacity="0.55">{text("producing")}</text>
    </Box>
  );
}

const LABELS = {
  bypass: "Bypass diodes",
  linear: "Linear (area only)",
  none: "Not modelled",
  portrait: "Portrait",
  landscape: "Landscape",
  free_standing: "Simplified (masking angle)",
  row_aware: "Detailed (view factors)",
  physical: "Physical",
  ashrae: "ASHRAE",
  martin_ruiz: "Martín-Ruiz",
  sapm: "SAPM",
  faiman: "Faiman",
  auto: "Automatic",
  sandia: "Sandia",
  pvwatts: "PVWatts",
  ond_curve: "OND efficiency curve",
};



const helpFor = (options, value) =>
  options.find((o) => o.value === value)?.help ?? "";

// "datasheet" = the module carries this model's own coefficients.
// "generic"   = pvlib will substitute its literature constants, silently.
// undefined   = no capability information yet (module not loaded).
function sourceOf(capability, model) {
  if (!capability || model === "auto") return undefined;
  if ((capability.datasheet || []).includes(model)) return "datasheet";
  if ((capability.generic || []).includes(model)) return "generic";
  return undefined;
}

/** One settings row: explanation left, fixed-width control right. */
function SettingRow({
  id,
  label,
  value,
  options,
  onChange,
  capability,
  disabled,
  extraNote,
  children,
}) {
  const isGreek = useIsGreek();
  const translateText = useTranslateText();
  const text = (value) => modelText(value, isGreek, translateText);
  const src = sourceOf(capability, value);
  let note = "";
  if (src === "datasheet") {
    note = " Using this module's own coefficients.";
  } else if (src === "generic") {
    note = " This module's datasheet has no coefficients for it, so standard literature values are used.";
  }

  return (
    <Stack
      id={id}
      direction={{ xs: "column", sm: "row" }}
      spacing={{ xs: 1, sm: 2 }}
      alignItems={{ xs: "stretch", sm: "flex-start" }}
      justifyContent="space-between"
      sx={{ py: 1.5 }}
    >
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {text(label)}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", display: "block", lineHeight: 1.45, mt: 0.25 }}
        >
          {text(helpFor(options, value))}
          {text(note)}
        </Typography>
        {extraNote}
      </Box>

      <Box sx={{ flex: "0 0 auto", width: { xs: "100%", sm: CONTROL_WIDTH } }}>
        <FormControl fullWidth size="small" disabled={disabled}>
          <Select
            inputProps={{ "aria-label": text(label) }}
            sx={{ "& .MuiSelect-select": { whiteSpace: "normal" } }}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            renderValue={(v) => text(options.find((o) => o.value === v)?.label ?? v)}
          >
            {options.map((o) => {
              const s = sourceOf(capability, o.value);
              return (
                <MenuItem key={o.value} value={o.value} disabled={Boolean(o.disabled)}>
                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                    <span>{text(o.label)}</span>
                    {o.recommended && (
                      <Chip
                        size="small"
                        label={isGreek ? "Προεπιλογή" : "Default"}
                        color="success"
                        variant="outlined"
                        sx={{ height: 18, fontSize: 10, fontWeight: 700 }}
                      />
                    )}
                    {s === "datasheet" && (
                      <Chip
                        size="small"
                        label={text("From datasheet")}
                        color="info"
                        variant="outlined"
                        sx={{ height: 18, fontSize: 10, fontWeight: 700 }}
                      />
                    )}
                    {s === "generic" && (
                      <Chip
                        size="small"
                        label={text("Generic values")}
                        variant="outlined"
                        sx={{ height: 18, fontSize: 10 }}
                      />
                    )}
                  </Stack>
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
        {children}
      </Box>
    </Stack>
  );
}

/** Read-only provenance: what will actually run for this project. */
function ResolvedSummary({ rows }) {
  const isGreek = useIsGreek();
  const translateText = useTranslateText();
  const text = (value) => modelText(value, isGreek, translateText);
  return (
    <Box
      sx={{
        mt: 0.5,
        p: 1.5,
        borderRadius: 1,
        bgcolor: "action.hover",
        display: "grid",
        gap: 0.75,
        gridTemplateColumns: "1fr",
      }}
    >
      {rows.map(([k, v]) => (
        <Stack key={k} direction="column" spacing={0.25} sx={{ minWidth: 0 }}>
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", overflowWrap: "anywhere" }}
          >
            {text(k)}
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 0 }}>
            {text(v)}
          </Typography>
          {k === 'DC model' && <Typography variant="caption" color="text.secondary">
            {isGreek ? 'Επιλέγεται αυτόματα από τους διαθέσιμους συντελεστές του πλαισίου. Κάθε μοντέλο απαιτεί το αντίστοιχο σύνολο παραμέτρων.' : 'Selected automatically from the module’s available coefficients. Each model requires its corresponding parameter set.'}
          </Typography>}
        </Stack>
      ))}
    </Box>
  );
}

export default function PvAdvancedModeling({
  transpositionModel,
  aoiModel,
  thermalModel,
  spectralModel,
  acModel,
  arrayGeometry,
  nearShadingModel,
  nearShadingElectricalEffect,
  moduleOrientation,
  horizonProfile,
  // Where the stored profile was measured, and where the project is now. A
  // horizon is a property of a LOCATION, so a site that moves after one was
  // fetched leaves the two disagreeing -- and a confident "Ridge up to 12°"
  // over terrain from somewhere else is worse than no figure at all.
  horizonProfileSite = null,
  siteLat = null,
  siteLon = null,
  onFetchHorizon,
  onClearHorizon,
  horizonBusy = false,
  horizonError = "",
  onChange,
  onReset,
  capability,
  moduleName,
  // Whether the selected module is bifacial. It decides which code path runs,
  // not just how much energy comes back: a bifacial module goes through the
  // row-field model whatever the geometry setting below says.
  isBifacial: selectedModuleIsBifacial = false,
  // True on the Modelling tab, where this is the page rather than a footnote.
  defaultExpanded = false,
  mountingConfig,
  installationType,
  thermalSettings,
  surfaceType,
  inverterName,
  inverterHasCurves,
  // Imported from an OND file: Automatic resolves to its efficiency curve.
  inverterHasOnd = false,
  // A fixed array whose shading is calculated from the physical layout: that
  // table holds the array's own rows, so "Not modelled" cannot take their
  // shadow out of it. The layout's shading applies whatever this says.
  layoutShadingIncludesRows = false,
  detailedShadingEnabled = false,
}) {
  const [sunPathOpen, setSunPathOpen] = useState(false);
  const isGreek = useIsGreek();
  const translateText = useTranslateText();
  const text = (value) => modelText(value, isGreek, translateText);
  const transposition = transpositionModel || DEFAULT_TRANSPOSITION;
  const aoi = aoiModel || DEFAULT_AOI_MODEL;
  const thermal = thermalModel || DEFAULT_THERMAL_MODEL;
  const thermalResolved = resolveThermalSettings(thermal, thermalSettings, mountingConfig, installationType);
  const spectral = spectralModel || DEFAULT_SPECTRAL_MODEL;
  const ac = acModel || DEFAULT_AC_MODEL;
  // What the project has SAVED, and what will actually run. They part company on
  // a bifacial module: the rear side has to be modelled, which only the detailed
  // row field does, so that is the model regardless of this setting.
  //
  // The saved value is deliberately left alone rather than rewritten to
  // "row_aware". If the module is later changed to a monofacial one, the project
  // gets its own choice back -- and with it Perez, which the detailed model
  // cannot use. Overwriting it would silently cost that project ~2% of its sky
  // model with nothing on screen to explain why.
  // An opaque coplanar roof disables rear collection and the row-field path
  // in the engine, even when the selected module is bifacial.
  const coplanarRoof = installationType === "roof_flush";
  const isBifacial = selectedModuleIsBifacial && !coplanarRoof;
  const storedGeometry = arrayGeometry || DEFAULT_ARRAY_GEOMETRY;
  const geometry = coplanarRoof || detailedShadingEnabled
    ? "free_standing" : isBifacial ? "row_aware" : storedGeometry;
  const nearShading = nearShadingModel || DEFAULT_NEAR_SHADING_MODEL;
  const nearShadingOptions = layoutShadingIncludesRows
    ? NEAR_SHADING_OPTIONS.map((option) => (option.value === "none" ? { ...option, disabled: true } : option))
    : NEAR_SHADING_OPTIONS;
  const modOrientation = moduleOrientation || DEFAULT_MODULE_ORIENTATION;
  const nearShadingEffect =
    nearShadingElectricalEffect == null || Number.isNaN(Number(nearShadingElectricalEffect))
      ? DEFAULT_NEAR_SHADING_EFFECT
      : Number(nearShadingElectricalEffect);

  // Summarise the stored terrain profile for the chip and the copy. Absent or
  // malformed means "not set" rather than an error: the profile is optional.
  const horizonSummary = useMemo(() => {
    const entries = Object.entries(horizonProfile || {})
      .map(([az, el]) => [Number(az), Number(el)])
      .filter(([az, el]) => Number.isFinite(az) && Number.isFinite(el));
    if (!entries.length) return null;
    const maxElevation = Math.max(...entries.map(([, el]) => el));
    return { points: entries.length, maxElevation, isFlat: maxElevation < 1 };
  }, [horizonProfile]);

  const horizonSiteMismatch = useMemo(() => {
    if (!horizonProfile || !horizonProfileSite) return false;
    if (siteLat == null || siteLon == null) return false;
    return (
      Math.abs(Number(horizonProfileSite.lat) - Number(siteLat)) > 1e-4 ||
      Math.abs(Number(horizonProfileSite.lon) - Number(siteLon)) > 1e-4
    );
  }, [horizonProfile, horizonProfileSite, siteLat, siteLon]);

  // Two thermal states count as the defaults: the one a new project starts
  // from (Uc/Uv following the installation type) and the one every project
  // saved before it carries (SAPM, no thermal settings).
  const thermalIsNewDefault = thermal === NEW_PROJECT_THERMAL_MODEL &&
    isNewProjectThermalSettings(thermalSettings);
  const thermalIsLegacyDefault = thermal === DEFAULT_THERMAL_MODEL && !Object.keys(thermalSettings || {}).length;
  const isDefault =
    transposition === DEFAULT_TRANSPOSITION &&
    aoi === DEFAULT_AOI_MODEL &&
    (thermalIsNewDefault || thermalIsLegacyDefault) &&
    ac === DEFAULT_AC_MODEL &&
    storedGeometry === DEFAULT_ARRAY_GEOMETRY;

  // Sandia can only run if the inverter carries fitted coefficients. The engine
  // refuses an impossible request and falls back to inference, so the UI says so
  // rather than letting the user believe a setting took effect.
  const acRequestImpossible = (ac === "sandia" && inverterHasCurves === false)
    || (ac === "ond_curve" && !inverterHasOnd && inverterHasCurves !== undefined);
  const effectiveAc = acRequestImpossible
    ? (inverterHasCurves ? "sandia" : "pvwatts")
    : ac === "auto"
      ? (inverterHasOnd ? "ond_curve" : inverterHasCurves ? "sandia" : inverterHasCurves === false ? "pvwatts" : null)
      : ac;

  // For a SAPM module, sapm_effective_irradiance applies the datasheet's own
  // B0-B5 angular response, so whatever is selected here is discarded. Saying
  // so is the whole point — a silently ignored control is worse than none.
  const iamInert = Boolean(capability?.is_sapm) && !isBifacial;
  const sapmBifacialSpectrumUnavailable = Boolean(capability?.is_sapm) && isBifacial;
  const autoResolvesTo = capability?.auto_resolves_to;

  // All four defaults in ONE update. Sequential onChange calls do not work here:
  // the parent derives the next config from the config of the current render, so
  // four calls in the same tick all start from the same base and only the last
  // survives — which looked like the button doing nothing.
  const RESET_ENTRIES = [
    ["transpositionModel", DEFAULT_TRANSPOSITION],
    ["aoiModel", DEFAULT_AOI_MODEL],
    ["thermalModel", NEW_PROJECT_THERMAL_MODEL],
    ["thermalSettings", NEW_PROJECT_THERMAL_SETTINGS],
    ["spectralModel", DEFAULT_SPECTRAL_MODEL],
    ["acModel", DEFAULT_AC_MODEL],
    ["arrayGeometry", DEFAULT_ARRAY_GEOMETRY],
    ["nearShadingModel", DEFAULT_NEAR_SHADING_MODEL],
    ["nearShadingElectricalEffect", DEFAULT_NEAR_SHADING_EFFECT],
    ["moduleOrientation", DEFAULT_MODULE_ORIENTATION],
  ];

  const reset = () => {
    if (onReset) {
      onReset(RESET_ENTRIES);
      return;
    }
    // Fallback for callers that batch updates themselves.
    RESET_ENTRIES.forEach(([k, v]) => onChange(k, v));
  };

  const effectiveIam = iamInert
    ? (isGreek ? "SAPM, συντελεστές B0–B5 του πλαισίου" : "SAPM, from the module's B0–B5")
    : aoi === "auto"
      ? `${text(LABELS[autoResolvesTo] || "Automatic")}${autoResolvesTo ? (isGreek ? " (αυτόματα)" : " (automatic)") : ""}`
      : text(LABELS[aoi] || aoi);

  // Which sky model the run will really use. The row-field model implements
  // isotropic and haydavies only and does not complain about anything else, so
  // a project set to Perez is quietly modelled on a different sky. Two ways in:
  // the geometry setting, and a bifacial module, which routes there regardless.
  // On a bifacial module the simplified model is not a choice that loses a
  // little accuracy -- it is a model that cannot represent the array at all,
  // because it has no rear side. So it is offered greyed out with the reason
  // attached, and the detailed row field carries the recommendation instead of
  // being the odd one out. Leaving both selectable meant the panel presented a
  // control that could not change anything.
  const geometryOptions = useMemo(() => {
    if (detailedShadingEnabled) return [{ value: "free_standing", label: isGreek ? "Πραγματική σκηνή του έργου" : "Actual project scene" }];
    if (!isBifacial) return GEOMETRY_OPTIONS;
    return GEOMETRY_OPTIONS.map((option) =>
      option.value === "row_aware"
        ? { ...option, recommended: true }
        : {
            ...option,
            recommended: false,
            disabled: true,
            help:
              "Front side only, so it cannot model a bifacial array. " +
              "Unavailable while a bifacial module is selected.",
          },
    );
  }, [isBifacial, detailedShadingEnabled, isGreek]);

  const runsRowField = !detailedShadingEnabled && (isBifacial || geometry === "row_aware");
  const skySubstituted = runsRowField && !ROW_FIELD_SKY.includes(transposition);
  const skyUsed = skySubstituted ? "haydavies" : transposition;
  const skyLabel = (value) =>
    text(TRANSPOSITION_OPTIONS.find((o) => o.value === value)?.label || value);

  const summaryRows = [
    [
      "Row sky masking",
      detailedShadingEnabled ? (isGreek ? "Πραγματική σκηνή του έργου" : "Actual project scene") : isBifacial
        ? (isGreek ? "Μοντέλο σειρών λόγω πλαισίου διπλής όψης" : "row field (set by the bifacial module, not by this choice)")
        : text(LABELS[geometry] || geometry),
    ],
    [
      "Row shadow",
      nearShading === "bypass"
        ? `${text(LABELS[nearShading])} · ${text(LABELS[modOrientation])} · ${Math.round(
            nearShadingEffect * 100
          )}% ${isGreek ? "ηλεκτρική επίδραση" : "electrical"}`
        : text(LABELS[nearShading] || nearShading),
    ],
    [
      "Horizon shading",
      horizonSummary
        ? horizonSummary.isFlat
          ? (isGreek ? "Επίπεδο έδαφος" : "flat terrain")
          : (isGreek ? `Ανάγλυφο έως ${horizonSummary.maxElevation.toFixed(0)}°` : `terrain up to ${horizonSummary.maxElevation.toFixed(0)}°`)
        : (isGreek ? "Δεν εφαρμόζεται" : "not applied"),
    ],
    [
      "Transposition",
      skySubstituted
        ? `${skyLabel(transposition)} \u2192 ${skyLabel(skyUsed)} (${isGreek ? "μοντέλο σειρών" : "row-field model"})`
        : skyLabel(transposition),
    ],
    ["Incidence angle", effectiveIam],
    [
      "Cell temperature",
      `${thermal === "pvsyst" ? "PVsyst" : thermal === "sapm" ? "SAPM" : "Faiman"} · ${thermalResolved.mode === "custom" ? (isGreek ? "Προσαρμοσμένο" : "Custom") : thermalPresetLabel(thermalResolved.preset, isGreek)}`,
    ],
    [
      isGreek ? "Θερμικοί συντελεστές" : "Thermal coefficients",
      Object.entries(thermalResolved.parameters).map(([k, v]) => `${k === "deltaT" ? "ΔT" : k} = ${v}`).join(" · "),
    ],
    [
      "Conversion (AC)",
      effectiveAc
        ? `${text(LABELS[effectiveAc])}${ac === "auto" ? (isGreek ? " (αυτόματα)" : " (automatic)") : ""}`
        : (isGreek ? "Καθορίζεται από τον αντιστροφέα" : "resolved from the inverter"),
    ],
    ["DC model", capability?.dc_model ? capability.dc_model.toUpperCase() : (isGreek ? "Καθορίζεται από το πλαίσιο" : "resolved from the module")],
    ["Module", moduleName || (isGreek ? "Δεν επιλέχθηκε" : "not selected")],
    ["Inverter", inverterName || (isGreek ? "Δεν επιλέχθηκε" : "not selected")],
    ["Ground surface", surfaceType || (isGreek ? "Ορίζεται στη σελίδα Γενικά" : "set on the General page")],
  ];

  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      disableGutters
      elevation={0}
      sx={{
        mt: 1.5,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{ minHeight: 46, "& .MuiAccordionSummary-content": { my: 0.75 } }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ minWidth: 0, flexWrap: "wrap" }}
          useFlexGap
        >
          <TuneIcon fontSize="small" sx={{ color: "text.secondary" }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {isGreek ? "Μοντέλα υπολογισμού" : "Calculation models"}
          </Typography>
          <Chip
            size="small"
            label={isDefault ? (isGreek ? "Προεπιλογές" : "Default settings") : (isGreek ? "Προσαρμοσμένα" : "Customised")}
            color={isDefault ? "default" : "warning"}
            variant={isDefault ? "outlined" : "filled"}
            sx={{ height: 20, fontSize: 11, fontWeight: 700 }}
          />
          {iamInert && (
            <Chip
              size="small"
              label={text("IAM set by module")}
              variant="outlined"
              sx={{ height: 20, fontSize: 11 }}
            />
          )}
        </Stack>
      </AccordionSummary>

      <AccordionDetails sx={{ pt: 0 }}>
        <Box sx={{ display: "grid", gridTemplateColumns: "minmax(0, 7fr) minmax(280px, 3fr)", gap: 3, alignItems: "start" }}>
        <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", display: "block", mb: 0.5 }}
        >
          {isGreek ? "Οι ενεργές παραδοχές εμφανίζονται δεξιά. Κάθε μοντέλο διαθέτει τις δικές του ρυθμίσεις." : "Effective assumptions appear on the right. Each model exposes its own settings."}
        </Typography>

        <Divider />

        <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 2 }}>
          {isGreek ? "Ακτινοβολία & σκιάσεις" : "Irradiance & shading"}
        </Typography>
        <SettingRow
          label="Row sky masking"
          value={geometry}
          options={geometryOptions}
          onChange={(v) => onChange("arrayGeometry", v)}
          extraNote={
            <>
              <Typography
                variant="caption"
                sx={{ display: "block", mt: 0.5, color: "text.secondary" }}
              >
                {detailedShadingEnabled
                  ? (isGreek ? "Η μπροστινή σκίαση και η ορατότητα ουρανού/εδάφους υπολογίζονται από τη σκηνή του έργου." : "Front shading and sky/ground visibility are calculated from the project scene.")
                  : geometry === "row_aware"
                  ? text("Uses the row spacing and height from the layout. Within 0.3% of the simplified model on the same sky model.")
                  : text("Uses the tilt and ground coverage ratio. Within 0.3% of the detailed model on the same sky model.")}
              </Typography>
              {isBifacial && !detailedShadingEnabled && (
                <Alert
                  severity="info"
                  icon={false}
                  sx={{ mt: 1, py: 0.35, "& .MuiAlert-message": { py: 0.35 } }}
                >
                  <Typography variant="caption">
                    {moduleName ? <b>{moduleName}</b> : text("This module")}{text(" is bifacial, so the rear side has to be modelled — which only the detailed row field does. It is set for you here, and the simplified model is unavailable until a monofacial module is selected.")}</Typography>
                </Alert>
              )}
              {!isBifacial && geometry === "row_aware" && transposition === "perez" && (
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mt: 1, py: 0.35, "& .MuiAlert-message": { py: 0.35 } }}
                >
                  <Typography variant="caption">{text("The detailed model cannot use ")}<b>Perez</b>{isGreek ? ", οπότε το " : ", so "}<b>Hay-Davies</b>
                    {" "}{text("is used for the sky instead — about 2% lower. That costs more than the 0.3% the detailed model gains. Switch to the simplified model to keep Perez, or pick Hay-Davies above so the sky model on record matches what runs.")}</Typography>
                </Alert>
              )}
            </>
          }
        />

        <SettingRow
          label="Row shadow on the panel"
          value={nearShading}
          options={nearShadingOptions}
          onChange={(v) => onChange("nearShadingModel", v)}
          extraNote={<>
            <Typography
              variant="caption"
              sx={{ display: "block", mt: 0.5, color: "text.secondary" }}
            >{text("Separate from the sky masking above: that is the light rows hide, this is the shadow they cast. Only bites at low sun, and grows sharply as rows get closer.")}</Typography>
            {layoutShadingIncludesRows && (nearShading === "none" ? (
              <Alert severity="warning" icon={false} sx={{ mt: 1, py: 0.35, "& .MuiAlert-message": { py: 0.35 } }}>
                <Typography variant="caption">{text("“Not modelled” does not apply to this layout: its calculated shading already includes the rows, with the obstacles and the other faces, and it is applied. Choose a shadow model, so the row shadow is not counted a second time in the DC shading percentage.")}</Typography>
              </Alert>
            ) : (
              <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "text.secondary" }}>
                {text("“Not modelled” is not offered: the layout's calculated shading includes the rows themselves.")}
              </Typography>
            ))}
          </>}
        />

        {nearShading === "bypass" && (
          <SettingRow
            label="Module orientation"
            value={modOrientation}
            options={MODULE_ORIENTATION_OPTIONS}
            onChange={(v) => onChange("moduleOrientation", v)}
            extraNote={<OrientationDiagram />}
          />
        )}

        {/* Only meaningful for the diode model, and it moves the answer a lot,
            so it is a visible control rather than a hidden constant. */}
        {nearShading === "bypass" && (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={{ xs: 1, sm: 2 }}
            alignItems={{ xs: "stretch", sm: "flex-start" }}
            justifyContent="space-between"
            sx={{ py: 1.5 }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{text("Electrical effect")}</Typography>
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", display: "block", lineHeight: 1.45, mt: 0.25 }}
              >{text("How completely a shaded substring drops out. 100% is the textbook diode behaviour; lower values blend toward a plain area loss, which is closer to what real arrays show because cells are not perfectly matched.")}</Typography>
            </Box>
            <Box sx={{ flex: "0 0 auto", width: { xs: "100%", sm: CONTROL_WIDTH } }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Slider aria-label={text("Electrical effect")}
                  size="small"
                  min={0}
                  max={1}
                  step={0.05}
                  value={nearShadingEffect}
                  onChange={(_e, v) => onChange("nearShadingElectricalEffect", v)}
                  sx={{ flex: 1 }}
                />
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 700, minWidth: 38, textAlign: "right" }}
                >
                  {Math.round(nearShadingEffect * 100)}%
                </Typography>
              </Stack>
            </Box>
          </Stack>
        )}

        <Divider />

        {/* Terrain shading. Not a dropdown: it is either fetched for this site
            or absent, so the control is the fetch itself. */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={{ xs: 1, sm: 2 }}
          alignItems={{ xs: "stretch", sm: "flex-start" }}
          justifyContent="space-between"
          sx={{ py: 1.5 }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }} useFlexGap>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{text("Horizon (terrain) shading")}</Typography>
              {horizonSiteMismatch && (
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mt: 1, py: 0.35, "& .MuiAlert-message": { py: 0.35 } }}
                >
                  <Typography variant="caption">{text("This terrain profile was measured at")}{" "}
                    {Number(horizonProfileSite.lat).toFixed(4)},{" "}
                    {Number(horizonProfileSite.lon).toFixed(4)}{text(" — the project is now at ")}{Number(siteLat).toFixed(4)}, {Number(siteLon).toFixed(4)}{text(". A horizon belongs to a location, so this one does not describe this site. Refresh it.")}</Typography>
                </Alert>
              )}
              {horizonSummary && (
                <Chip
                  size="small"
                  variant="outlined"
                  color={horizonSummary.isFlat ? "default" : "info"}
                  label={
                    horizonSummary.isFlat
                      ? text("Flat terrain")
                      : (isGreek ? `Ανάγλυφο έως ${horizonSummary.maxElevation.toFixed(0)}°` : `Ridge up to ${horizonSummary.maxElevation.toFixed(0)}°`)
                  }
                  sx={{ height: 20, fontSize: 11, fontWeight: 700 }}
                />
              )}
            </Stack>
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", display: "block", lineHeight: 1.45, mt: 0.25 }}
            >
              {horizonSummary
                ? horizonSummary.isFlat
                  ? text("The land around this site is level, so the terrain profile makes no difference to the result.")
                  : text("Hills block the sun early and late in the day. Those hours are removed from the direct beam.")
                : text("Not set. Fetch the surrounding terrain to account for hills blocking the sun at low angles. Makes no difference on level ground.")}
            </Typography>
            {horizonError && (
              <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "error.main" }}>
                {isGreek ? (/\p{Script=Greek}/u.test(horizonError) ? horizonError : "Δεν ήταν δυνατή η λήψη του αναγλύφου. Δοκιμάστε ξανά.") : horizonError}
              </Typography>
            )}
          </Box>

          <Box sx={{ flex: "0 0 auto", width: { xs: "100%", sm: CONTROL_WIDTH } }}>
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="outlined"
                sx={{ minWidth: 0, minHeight: 26, px: 1, py: 0.15, fontSize: "0.72rem" }}
                onClick={onFetchHorizon}
                disabled={horizonBusy || !onFetchHorizon}
                startIcon={horizonBusy ? <CircularProgress size={14} /> : null}
              >
                {horizonBusy ? text("Fetching…") : horizonSummary ? text("Refresh") : text("Fetch terrain")}
              </Button>
              {horizonSummary && (
                <Button size="small" color="warning" onClick={onClearHorizon}
                  sx={{ minWidth: 0, minHeight: 26, px: 1, py: 0.15, fontSize: "0.72rem" }}>{text("Remove")}</Button>
              )}
            </Stack>
          </Box>
        </Stack>

        {/* The horizon, on the sun path it actually obstructs.
            A skyline in degrees says nothing about what it costs; the overlay
            says which hours of which months it takes, which is the question the
            far-shading line in the loss diagram raises and cannot answer. */}
        {/* Full size in a dialog rather than inline: at the width of this column
            the seven tracks and their hour lines overlap into a smear. It is the
            same dialog the layout tab opens, so there is one chart, not two that
            can drift apart. */}
        {siteLat !== null && siteLat !== undefined && siteLat !== "" &&
          siteLon !== null && siteLon !== undefined && siteLon !== "" &&
          Number.isFinite(Number(siteLat)) && Number.isFinite(Number(siteLon)) && (
          <Box sx={{ pb: 1.5 }}>
            <Button size="small" variant="outlined" onClick={() => setSunPathOpen(true)}
              sx={{ minWidth: 0, minHeight: 26, px: 1, py: 0.15, fontSize: "0.72rem" }}>{text("View sun path and terrain")}</Button>
            <SunPathDialog
              open={sunPathOpen}
              onClose={() => setSunPathOpen(false)}
              lat={siteLat}
              lon={siteLon}
              profile={horizonProfile}
            />
          </Box>
        )}

        <Divider />

        {/* No capability passed for transposition on purpose: it is a
            sky-diffuse model driven by irradiance and sun geometry, so the
            module plays no part in it. */}
        <SettingRow
          id="field-pv-model-transposition"
          label="Transposition (diffuse sky)"
          value={transposition}
          options={TRANSPOSITION_OPTIONS.map((o) => ({ ...o, disabled: runsRowField && !ROW_FIELD_SKY.includes(o.value) }))}
          onChange={(v) => onChange("transpositionModel", v)}
          extraNote={
            detailedShadingEnabled && isBifacial ? (
              <Alert severity="info" sx={{ mt: 1 }}>
                {isGreek
                  ? `Μπροστινή όψη: ${skyLabel(transposition)}, με αναλυτική σκίαση της σκηνής. Πίσω όψη: ${ROW_FIELD_SKY.includes(transposition) ? skyLabel(transposition) : "Hay-Davies"}, με μοντέλο κανονικών σειρών και ομοιόμορφο φωτισμό ανά προσανατολισμό. Οι πίσω απώλειες ορίζονται στον Εξοπλισμό.`
                  : `Front: ${skyLabel(transposition)}, with detailed scene shading. Rear: ${ROW_FIELD_SKY.includes(transposition) ? skyLabel(transposition) : "Hay-Davies"}, with a regular-row model and uniform light per orientation. Rear allowances are set under Equipment.`}
              </Alert>
            ) : skySubstituted ? (
              <Alert
                severity="warning"
                icon={false}
                sx={{ mt: 1, py: 0.35, "& .MuiAlert-message": { py: 0.35 } }}
              >
                <Typography variant="caption">{text("This run uses ")}<b>{skyLabel(skyUsed)}</b>{text(", not")}{" "}
                  <b>{skyLabel(transposition)}</b>.{" "}
                  {isBifacial
                    ? text("A bifacial module is modelled as a row field, and that model implements Hay-Davies and isotropic only.")
                    : text("The detailed row model implements Hay-Davies and isotropic only.")}{" "}{text("Hay-Davies runs about 1-2% below Perez on a tilted plane, and the report states the model that ran. Pick Hay-Davies here to make the setting on record match it.")}</Typography>
              </Alert>
            ) : null
          }
        />

        <Divider />

        <SettingRow
          id="field-pv-model-aoi"
          label="Incidence angle (IAM)"
          value={aoi}
          options={AOI_OPTIONS}
          onChange={(v) => onChange("aoiModel", v)}
          capability={iamInert ? null : capability}
          disabled={iamInert}
          extraNote={
            iamInert ? (
              <Alert
                severity="info"
                icon={false}
                sx={{ mt: 1, py: 0.35, "& .MuiAlert-message": { py: 0.35 } }}
              >
                <Typography variant="caption">
                  {moduleName ? <b>{moduleName}</b> : text("This module")}{text(" is a SAPM module, so its angular response comes from the datasheet's own B0–B5 coefficients and this setting has no effect.")}</Typography>
              </Alert>
            ) : aoi === "auto" && autoResolvesTo ? (
              <Typography
                variant="caption"
                sx={{ display: "block", mt: 0.5, color: "text.secondary" }}
              >{text("For this module, Automatic resolves to")}{" "}
                <b>{text(LABELS[autoResolvesTo] || autoResolvesTo)}</b>
                {capability?.dc_model ? (isGreek ? ` (μοντέλο DC: ${capability.dc_model.toUpperCase()}).` : ` (${capability.dc_model.toUpperCase()} DC model).`) : "."}
              </Typography>
            ) : null
          }
        />

        <Divider />

        <SettingRow
          id="field-pv-model-spectral"
          label="Spectral correction"
          value={sapmBifacialSpectrumUnavailable ? "none" : spectral}
          disabled={sapmBifacialSpectrumUnavailable}
          extraNote={sapmBifacialSpectrumUnavailable ? (
            <Typography variant="caption">
              {isGreek
                ? "Στη διπλής όψης διαδρομή με ηλεκτρικό μοντέλο SAPM δεν εφαρμόζεται φασματική διόρθωση. Η αναφορά καταγράφει αυτή την εξαίρεση."
                : "Spectral correction is not applied on the bifacial path with the SAPM electrical model. The report records this exception."}
            </Typography>
          ) : null}
          options={SPECTRAL_OPTIONS.map((option) => iamInert && option.value === "first_solar"
            ? { ...option, label: isGreek ? "SAPM — ενσωματωμένη διόρθωση" : "SAPM — built-in correction",
                help: isGreek ? "Χρησιμοποιεί τους φασματικούς συντελεστές A0–A4 του πλαισίου. Η επιλογή χωρίς διόρθωση απενεργοποιεί τη φασματική επίδραση." : "Uses the module's A0–A4 spectral coefficients. Not applied disables the spectral effect." }
            : option)}
          onChange={(v) => onChange("spectralModel", v)}
        />

        <Divider />

        <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 2 }}>
          {isGreek ? "Θερμοκρασία πλαισίων" : "Module temperature"}
        </Typography>
        <SettingRow id="field-pv-model-thermal" label="Cell temperature" value={thermal} options={THERMAL_OPTIONS}
          onChange={(v) => onChange("thermalModel", v)} />
        <PvThermalSettings model={thermal} settings={thermalSettings} mounting={mountingConfig}
          installation={installationType} onChange={onChange} />
        <Divider />

        <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 2 }}>
          {isGreek ? "Αντιστροφή DC/AC" : "Electrical conversion"}
        </Typography>
        <SettingRow
          label="Conversion (inverter AC model)"
          value={ac}
          options={AC_OPTIONS.map((o) => ({ ...o, disabled: (o.value === "sandia" && inverterHasCurves === false) || (o.value === "ond_curve" && !inverterHasOnd) }))}
          onChange={(v) => onChange("acModel", v)}
          extraNote={
            acRequestImpossible ? (
              <Alert
                severity="warning"
                icon={false}
                sx={{ mt: 1, py: 0.35, "& .MuiAlert-message": { py: 0.35 } }}
              >
                <Typography variant="caption">
                  {inverterName ? <b>{inverterName}</b> : (isGreek ? "Ο αντιστροφέας" : "This inverter")}{" "}
                  {ac === "ond_curve"
                    ? (isGreek
                      ? "δεν έχει εισαχθεί από αρχείο OND, οπότε χρησιμοποιείται η αυτόματη επιλογή."
                      : "was not imported from an OND file, so the automatic choice is used instead.")
                    : isGreek
                    ? "δεν διαθέτει προσαρμοσμένες καμπύλες απόδοσης. Το μοντέλο Sandia δεν μπορεί να εφαρμοστεί και χρησιμοποιείται το PVWatts. Προσθέστε τις καμπύλες στα στοιχεία του αντιστροφέα για να το ενεργοποιήσετε."
                    : "has no fitted efficiency curves, so Sandia cannot run and PVWatts is used instead. Add the curves from the inverter details dialog to enable it."}
                </Typography>
              </Alert>
            ) : ac === "auto" && effectiveAc ? (
              <Typography
                variant="caption"
                sx={{ display: "block", mt: 0.5, color: "text.secondary" }}
              >
                {isGreek ? "Για αυτόν τον αντιστροφέα, η αυτόματη επιλογή καταλήγει στο" : "For this inverter, Automatic resolves to"}{" "}
                <b>{text(LABELS[effectiveAc])}</b>.
              </Typography>
            ) : null
          }
        />

        <Divider sx={{ mb: 1.5 }} />

        </Box>
        <Box component="aside" sx={{ position: "sticky", top: 20, minWidth: 0, border: "1px solid", borderColor: "divider", borderRadius: 2, p: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
          {isGreek ? "Ενεργές παραδοχές" : "Active model configuration"}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          {isGreek ? "Ρυθμίσεις για την επόμενη εκτέλεση. Οι αλλαγές δεν ενημερώνουν προηγούμενα αποτελέσματα· απαιτείται νέα εκτέλεση." : "Settings for the next run. Changes do not update previous results; run the calculation again."}
        </Typography>
        <ResolvedSummary rows={summaryRows} />

        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ mt: 1.75, flexWrap: "wrap" }}
          useFlexGap
        >
          <Button size="small" onClick={reset} disabled={isDefault}>
            {isGreek ? "Επαναφορά μοντέλων" : "Reset model defaults"}
          </Button>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {isGreek ? "Κοινές ρυθμίσεις για παραγωγή και μελέτη σκοπιμότητας." : "Applies to both market dispatch and feasibility runs."}
          </Typography>
        </Stack>
        </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}
